import clsx from 'clsx'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import { useEffect, useRef } from 'react'

import Icon from '../Icon'
import ActionIcon from '../ActionIcon'

import styles from './style.module.scss'

/**
 * Drei-Punkte-Menü mit den Handlungen einer Zeile.
 *
 * Zwei Feinheiten, ohne die das Menü stumm bleibt:
 *
 * 1. **Die Kennung muss über Neu-Renderings hinweg gleich bleiben.** Sie wurde
 *    bei jedem Rendern neu gewürfelt; `react-tooltip` bindet den Klick aber
 *    beim Einhängen an die damalige Kennung. Nach dem ersten Neu-Rendering —
 *    und ein Liste rendert bei jedem geladenen Datensatz neu — zeigte
 *    `data-for` ins Leere und der Klick tat nichts.
 * 2. **Nach dem Einhängen `rebuild()`.** Bis dahin funktionierte das Menü nur
 *    zufällig: Die Navigationsleiste ruft `rebuild()` bei jedem eigenen
 *    Rendern auf und band dabei nebenbei fremde Menüs mit. Rendert sie nicht
 *    mit, blieb das Menü ohne Wirkung.
 *
 * Seit 02.11.2026 steht die Liste linksbündig, und ein Eintrag kann ein
 * Symbol tragen (`icon`, Name aus `UI/ActionIcon`). Zentriert standen die
 * Einträge verschieden weit eingerückt da; man las jede Zeile neu an.
 * Einträge ohne Symbol bekommen einen leeren Platz gleicher Breite, sobald
 * ein anderer Eintrag derselben Liste eines hat — sonst sprängen die Texte.
 */
export default function BtnTooltip(props) {
	// useRef statt useMemo: Der Wert darf sich unter keinen Umständen ändern.
	const idRef = useRef(null)
	if (idRef.current === null) idRef.current = `tooltip-${Math.random().toString(36).slice(2)}`
	const id = idRef.current

	useEffect(() => {
		ReactTooltip.rebuild()
	}, [])

	const withIcons = props.items.some(item => item.icon)

	return (
		<div className={props.className}>
			<button
				// https://github.com/wwayne/react-tooltip/issues/377
				onMouseEnter={() => ReactTooltip.hide()}
				className={styles.btnActive}
				data-tip
				data-event="click"
				data-for={id}
			>
				<Icon name="threeDots" color="#008B9C" />
			</button>
			<ReactTooltip
				id={id}
				clickable
				className={styles.tooltip}
				globalEventOff="click"
				getContent={() => (
					<div className={styles.bthList}>
						{props.items.map((item, index) => (
							<button
								disabled={item.disabled}
								style={item.style}
								className={clsx(styles.btnItem, item.className)}
								key={index}
								onClick={event => {
									// Erst schliessen, dann handeln. Das Menue liegt in einem
									// `clickable`-Tooltip; ein Klick darin loest `globalEventOff`
									// nicht aus. Oeffnete die Handlung ein Fenster, blieb das
									// Menue deshalb sichtbar darueber stehen.
									ReactTooltip.hide()
									item.onClick(event)
								}}
							>
								{withIcons && (
									<span className={styles.itemIcon}>
										{item.icon && <ActionIcon name={item.icon} size={18} />}
									</span>
								)}
								<span className={styles.itemLabel}>{item.label}</span>
							</button>
						))}
					</div>
				)}
			/>
		</div>
	)
}

BtnTooltip.propTypes = {
	className: PropTypes.string,
	items: PropTypes.arrayOf(
		PropTypes.shape({
			disabled: PropTypes.bool,
			className: PropTypes.string,
			icon: PropTypes.string,
			label: PropTypes.string.isRequired,
			onClick: PropTypes.func.isRequired
		}).isRequired
	).isRequired
}

BtnTooltip.defaultProps = {
	className: ''
}
